import type {
  PostProductionParameterValue,
  PostProductionParameters,
  PostProductionParametersSchema,
  PostProductionSchemaProperty,
} from "@/lib/workspace-api"
import { FieldHintTooltip } from "@/components/ui/field-hint-tooltip"
import { postProductionParameterFieldId } from "@/lib/post-production-parameters"
import { Input } from "@/components/ui/primitives"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import type { TFunction } from "i18next"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

type Props = {
  schema: PostProductionParametersSchema
  value: PostProductionParameters
  onChange: (value: PostProductionParameters) => void
  errors?: Record<string, string>
}

function propertyType(property: PostProductionSchemaProperty): string {
  if (Array.isArray(property.type)) return property.type[0] ?? "string"
  return property.type ?? "string"
}

function labelForKey(key: string, t: TFunction<"shot">) {
  const i18nKey = `postProduction.parameterLabels.${key}`
  const translated = t(i18nKey, { defaultValue: "" })
  if (translated && translated !== i18nKey) return translated

  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function optionLabel(key: string, option: string | number | boolean, t: TFunction<"shot">) {
  const i18nKey = `postProduction.parameterOptions.${key}.${String(option)}`
  const translated = t(i18nKey, { defaultValue: "" })
  if (translated && translated !== i18nKey) return translated
  return String(option)
}

function numericStep(property: PostProductionSchemaProperty) {
  return propertyType(property) === "integer" ? 1 : 0.01
}

function numericParameterHint(property: PostProductionSchemaProperty, t: TFunction<"shot">): string | null {
  const type = propertyType(property)
  if (type !== "number" && type !== "integer") return null

  const { minimum: min, maximum: max } = property
  if (min != null && max != null) {
    return t(
      type === "integer"
        ? "postProduction.parameterHint.integerBetween"
        : "postProduction.parameterHint.decimalBetween",
      { min, max },
    )
  }

  return null
}

function FieldLabel({ label, hint }: { label: string; hint?: string | null }) {
  if (!hint) {
    return <span className="font-medium text-ui-text-primary">{label}</span>
  }

  return (
    <span className="flex items-center gap-1 font-medium text-ui-text-primary">
      <span>{label}</span>
      <FieldHintTooltip label={hint} />
    </span>
  )
}

export function ParametersSchemaForm({ schema, value, onChange, errors = {} }: Props) {
  const { t } = useTranslation("shot")
  const properties = schema.properties ?? {}
  const entries = Object.entries(properties)

  if (entries.length === 0) {
    return <p className="text-xs text-ui-text-muted">{t("postProduction.noParameters")}</p>
  }

  const update = (key: string, next: PostProductionParameterValue | null) => {
    const copy = { ...value }
    if (next === null || next === "") {
      delete copy[key]
    } else {
      copy[key] = next
    }
    onChange(copy)
  }

  return (
    <TooltipPrimitive.Provider delayDuration={150}>
      <div className="grid gap-3 sm:grid-cols-2">
        {entries.map(([key, property]) => {
          const type = propertyType(property)
          const current = value[key] ?? property.default ?? ""
          const label = labelForKey(key, t)
          const description = property.description
          const numericHint = numericParameterHint(property, t)
          const isNumeric = type === "number" || type === "integer"
          const fieldError = errors[key]
          const hasDefault = property.default !== undefined && property.default !== null
          const enumValues = Array.isArray(property.enum) ? property.enum : []
          const singleFixedEnum = enumValues.length === 1

          return (
            <div
              key={key}
              id={postProductionParameterFieldId(key)}
              className="flex min-w-0 scroll-mt-3 flex-col gap-1.5 text-xs text-ui-text-secondary"
            >
              <label className="flex min-w-0 flex-col gap-1.5">
                <FieldLabel label={label} hint={isNumeric ? numericHint : null} />
                {enumValues.length > 0 ? (
                  <select
                    value={String(current)}
                    onChange={(event) => update(key, event.target.value)}
                    aria-invalid={fieldError ? true : undefined}
                    disabled={singleFixedEnum}
                    className={cn(
                      "h-9 rounded-lg border bg-ui-surface-sunken px-3 text-sm text-ui-text-primary focus:ring-2 focus:ring-[var(--ui-focus-ring)] focus:outline-none",
                      fieldError ? "border-ui-status-failed" : "border-ui-border",
                      singleFixedEnum && "opacity-80",
                    )}
                  >
                    {!hasDefault && !singleFixedEnum ? (
                      <option value="">{t("postProduction.defaultParameter")}</option>
                    ) : null}
                    {enumValues.map((option) => (
                      <option key={String(option)} value={String(option)}>
                        {optionLabel(key, option, t)}
                      </option>
                    ))}
                  </select>
                ) : type === "boolean" ? (
                  <span className="flex h-9 items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-ui-border-strong bg-ui-surface-sunken"
                      checked={Boolean(current)}
                      onChange={(event) => update(key, event.target.checked)}
                    />
                    <span className="text-ui-text-muted">{t("postProduction.enabledParameter")}</span>
                  </span>
                ) : type === "number" || type === "integer" ? (
                  <Input
                    type="number"
                    value={String(current)}
                    min={property.minimum}
                    max={property.maximum}
                    step={numericStep(property)}
                    aria-invalid={fieldError ? true : undefined}
                    onChange={(event) => {
                      const raw = event.target.value
                      update(key, raw === "" ? null : type === "integer" ? Number.parseInt(raw, 10) : Number(raw))
                    }}
                    className={cn("h-9 text-sm", fieldError && "border-ui-status-failed")}
                  />
                ) : type === "string" ? (
                  <Input
                    value={String(current)}
                    onChange={(event) => update(key, event.target.value)}
                    aria-invalid={fieldError ? true : undefined}
                    className={cn("h-9 text-sm", fieldError && "border-ui-status-failed")}
                  />
                ) : (
                  <span className="rounded-lg border border-ui-border bg-ui-surface-sunken px-3 py-2 text-ui-text-muted">
                    {t("postProduction.unsupportedParameterType", { type })}
                  </span>
                )}
                {description && !isNumeric ? (
                  <span className="text-[11px] leading-relaxed text-ui-text-muted">{description}</span>
                ) : null}
              </label>
              {fieldError ? <p className="text-[11px] text-ui-status-failed">{fieldError}</p> : null}
            </div>
          )
        })}
      </div>
    </TooltipPrimitive.Provider>
  )
}
